import { useEffect, useRef, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  cn,
  ErrorMessage,
  Field,
  FormLabel,
  Input,
  Label,
  Select,
} from '@quickmd-llc/design-system';
import { APIProvider } from '@vis.gl/react-google-maps';
import { PatientAddress } from '@types';
import { US_STATES } from '@constants';
import { parsePlaceResultToAddress } from './utils';
import { GooglePlaceAutocomplete } from './GooglePlaceAutocomplete';

export interface FormValues {
  address?: PatientAddress;
}
interface Props {
  disabled?: boolean;
  title: string;
  visitState?: string;
}

const ADDRESS_REQUIRED_ERROR = 'You must enter your address';
const STATE_MISMATCH_ERROR =
  'This address state does not match the visit you’ve reserved. You must provide an address that matches the state you selected earlier.';

export function AddressInput({
  disabled,
  title,
  visitState,
}: Props): JSX.Element {
  const {
    clearErrors,
    control,
    formState,
    getValues,
    register,
    setError,
    setValue,
    watch,
  } = useFormContext<FormValues>();
  const { errors } = formState;
  const [showFullAddress, setShowFullAddress] = useState(false);
  const stateCode = watch('address.stateCode');
  const ref = useRef<HTMLInputElement>(null);

  function setAddress(address: PatientAddress | undefined) {
    if (address?.streetAddress1) {
      setValue('address.streetAddress1', address.streetAddress1);
      setValue('address.streetAddress2', address.streetAddress2);
      setValue('address.zip', address.zip);
      setValue('address.city', address.city);
      setValue('address.stateCode', address.stateCode);
      setValue('address.countryCode', address.countryCode);

      clearErrors('address.streetAddress1');

      setShowFullAddress(true);

      if (ref.current) {
        ref.current.value = address.streetAddress1;
      }
    }
  }

  useEffect(() => {
    const address = getValues('address');
    setAddress(address);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!stateCode || !visitState) {
      return;
    }

    if (stateCode !== visitState) {
      setError('address.stateCode', {
        message: STATE_MISMATCH_ERROR,
      });
    } else if (stateCode === visitState) {
      clearErrors('address.stateCode');
    }
  }, [clearErrors, setError, stateCode, visitState]);

  function handleBlur({ currentTarget }: React.FocusEvent<HTMLInputElement>) {
    if (!currentTarget.value.trim()) {
      setValue('address', undefined);
      setError('address.streetAddress1', {
        message: ADDRESS_REQUIRED_ERROR,
      });
    }
  }

  function handleClearAddress() {
    setValue('address', undefined);
    setError('address.streetAddress1', {
      message: ADDRESS_REQUIRED_ERROR,
    });

    if (ref.current) {
      ref.current.value = '';
    }

    setShowFullAddress(false);
  }

  return (
    <div>
      <Controller
        control={control}
        name="address.streetAddress1"
        render={() => {
          const { address } = errors;
          const addressError = address?.streetAddress1?.message;
          const stateMismatchError = address?.stateCode?.message;
          const hasError = Boolean(addressError || stateMismatchError);

          return (
            <Field disabled={disabled} error={hasError}>
              <div
                className={cn('mb-3 flex items-center justify-between', {
                  'mb-0': hasError,
                })}
              >
                <FormLabel>{title}</FormLabel>

                {showFullAddress && (
                  <Label
                    className="h-min cursor-pointer align-middle !text-xs"
                    onClick={handleClearAddress}
                  >
                    Clear
                  </Label>
                )}
              </div>

              {hasError && (
                <ErrorMessage>
                  {addressError || stateMismatchError}
                </ErrorMessage>
              )}

              <APIProvider apiKey={import.meta.env.VITE_GOOGLE_PLACES_API_KEY}>
                <GooglePlaceAutocomplete
                  invalid={Boolean(addressError)}
                  placeholder="Enter your address"
                  inputRef={ref}
                  onBlur={handleBlur}
                  onPlaceSelected={(place) => {
                    const nextAddress = parsePlaceResultToAddress(place);
                    setAddress(nextAddress);
                  }}
                />
              </APIProvider>
            </Field>
          );
        }}
        rules={{
          required: {
            value: true,
            message: ADDRESS_REQUIRED_ERROR,
          },
        }}
      />

      {showFullAddress && (
        <div className="my-2 flex flex-col gap-2">
          <Input
            {...register('address.streetAddress2')}
            disabled={disabled}
            type="text"
            placeholder="Apartment, Suite, etc"
          />
          <Input
            {...register('address.city', { required: true })}
            disabled={disabled}
            type="text"
            placeholder="Enter your city"
            invalid={Boolean(errors?.address?.city)}
          />
          <div className="flex gap-2">
            <Select
              {...register('address.stateCode', stateCodeOptions(visitState))}
              disabled={disabled}
            >
              {US_STATES.map(({ name, abbreviation }) => (
                <option key={abbreviation} value={abbreviation}>
                  {name}
                </option>
              ))}
            </Select>
            <Input
              className="!w-32"
              disabled={disabled}
              {...register('address.zip', zipOptions)}
              type="number"
              placeholder="Zip"
              invalid={Boolean(errors?.address?.zip)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

const stateCodeOptions = (visitState?: string) => {
  return {
    required: true,
    validate: (value?: string): string | undefined => {
      if (visitState && value !== visitState) {
        return STATE_MISMATCH_ERROR;
      }
    },
  };
};

const zipOptions = {
  required: true,
  validate: (value?: string): boolean | undefined => {
    if (value?.length === 5) return;
    return false;
  },
};
